import { Col, Form, Modal, Row } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { InfoCard2 } from '../../../widgets/components/UI/InfoCard2'
import { ToolTipUIForModal } from '../../../widgets/components/UI/ToolTipUIForModal'
import { Field, Formik } from 'formik'
import TextInput from '../../../widgets/components/Input/TextInput'
import { useState } from 'react'
import { useAuth } from '../../../auth'
import { createDepartment, editDepartment } from '../../core/_requests'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import Skeleton from 'react-loading-skeleton'
import { InputPeriod } from '../../../widgets/components/Input/InputPeriod'
import { periodOptions } from '../../../../core/_constants'
import folderIcon from '../../../../../_metronic/assets/images/svg/data-room/blue-folder-icon.svg'

const AddProjectModal = ({
  isActive,
  setIsActive,
  selectedCategoryFolder,
  getProjectList,
  editDetailsData,
  setEditDetailsData,
  editDepartmentId,
  editDetailsLoading,
  clickedDepartment,
  clickedCategory,
}: any) => {
  const { formatMessage } = useIntl()
  const { companyId } = useAuth()

  const [isLoading, setLoading] = useState<boolean>(false)

  const initialValues = {
    projectname: editDetailsData?.name || '',
    projectValue: editDetailsData?.retentionValue || '',
    projectPeriod: editDetailsData?.retentionPeriod || 'Days',
    isLegalHold: editDetailsData?.legalHold || false,
    legalHoldExpiryPeriod: editDetailsData?.legalHold ? editDetailsData?.legalPeriod || '' : 'Days',
    legalHoldExpiryValue: editDetailsData?.legalHold ? editDetailsData?.legalValue || '' : '',
  }

  const userSchema = Yup.object().shape({
    projectname: Yup.string().nullable().required('Case name is required'),
    projectPeriod: Yup.string().nullable().required('Case period is required'),
    projectValue: Yup.number()
      .nullable()
      .integer('Retention value must be a integer number')
      .required('Retention value is required')
      .when('projectPeriod', {
        is: (value: string) => value === 'Days' || value === 'Years' || value === 'Months',
        then: Yup.number()
          .min(0, 'Value cannot be negative')
          .typeError('Retention Value must be a number'),
      }),

    isLegalHold: Yup.boolean(),

    legalHoldExpiryPeriod: Yup.string()
      .nullable()
      .when('isLegalHold', {
        is: true,
        then: (schema) => schema.required('LegalHoldExpiryPeriod is required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    legalHoldExpiryValue: Yup.number()
      .nullable()
      .integer('Legal value must be a integer number')
      .when(['isLegalHold', 'legalHoldExpiryPeriod'], {
        is: (isLegalHold: boolean, legalHoldExpiryPeriod: string) =>
          isLegalHold && ['Days', 'Years', 'Months'].includes(legalHoldExpiryPeriod),
        then: (schema) =>
          schema
            .min(0, 'LegalHoldValue cannot be negative')
            .typeError('LegalHoldExpiryValue must be a number')
            .required('LegalHoldExpiryValue is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    herwise: Yup.number().nullable(),
  })

  const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      if (!companyId) return
      const type = 'LevelThree'
      setLoading(true)
      const payload = {
        name: values?.projectname,
        retentionPeriod: values?.projectPeriod,
        retentionValue: parseInt(values?.projectValue),
        legalHold: values?.isLegalHold,
        legalPeriod: values?.isLegalHold ? values?.legalHoldExpiryPeriod : null,
        legalValue: values?.isLegalHold ? parseInt(values?.legalHoldExpiryValue) : null,
      }

      const sanitizedPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([_, value]) =>
            value !== undefined &&
            value !== null &&
            value !== '' &&
            !(typeof value === 'number' && isNaN(value))
        )
      )

      const changedValues = Object.fromEntries(
        Object.entries(payload).filter(
          ([key, value]) =>
            value !== undefined &&
            value !== null &&
            value !== '' &&
            !(typeof value === 'number' && isNaN(value)) &&
            value !== (initialValues as Record<string, any>)[key] // Cast initialValues to Record<string, any>
        )
      )

      if (editDetailsData && Object.keys(changedValues).length > 0) {
        const {
          data: { success, errors },
        } = await editDepartment(companyId, editDepartmentId, changedValues, type)
        if (success) {
          toast.success('Case edited successfully')
          resetForm()
          setIsActive(false)
          setEditDetailsData(null)
          getProjectList(selectedCategoryFolder)
        } else {
          toast.error(errors[0])
        }
      } else {
        const {
          data: { success, errors },
        } = await createDepartment(companyId, sanitizedPayload, type, selectedCategoryFolder)
        if (success) {
          toast.success('Case created successfully')
          resetForm()
          setIsActive(false)
          getProjectList(selectedCategoryFolder)
        } else {
          toast.error(errors[0])
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='mt-4'>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={userSchema}
        enableReinitialize={true}
        validateOnMount
        validateOnChange
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit}>
            <Modal
              show={isActive}
              onHide={() => {
                setIsActive(false)
                setEditDetailsData(null)
                formik.resetForm()
              }}
              size='lg'
              fullscreen
            >
              <Modal.Header closeButton>
                <h2>
                  {formatMessage({
                    id: editDetailsData ? 'Edit Projects, Cases, or Entities' : 'Add New Projects, Cases, or Entities',
                  })}
                </h2>
              </Modal.Header>
              <Modal.Body>
                <div className='container'>
                  <h5 className='mb-5'>
                    {' '}
                    <img src={folderIcon} className='me-2' alt='folderIcon' />
                    <>
                      <span className='color-blue'>{clickedDepartment}</span> <span>&gt;</span>{' '}
                      <span className='color-blue'>{clickedCategory}</span> <span>&gt;</span>{' '}
                      <span className='color-blue'>{editDetailsData?.name ? editDetailsData?.name : "Case"}</span>
                    </>
                  </h5>
                  <div className='d-flex gap-2 align-items-center'>
                    <h2 className='font-size-16 m-0'>{formatMessage({ id: 'Case Details' })}</h2>
                    <ToolTipUIForModal
                      tooltipText={formatMessage({
                        id: 'Set up the case here to create a Level 4 classification for organizing records in the RecordsKeeper system.',
                      })}
                    />
                  </div>

                  <div className='mt-4'>
                    <InfoCard2
                      title={'Creating a New case for Records Organization'}
                      desc={
                        'Use this page to add a new project to your RecordsKeeper system. Set a custom retention period and enable legal hold for certain documents to prevent modification or deletion. After the department is created, you can add categories for more detailed organization.'
                      }
                      slug={''}
                    />
                  </div>

                  {editDetailsLoading ? (
                    <div className='col-xl-12'>
                      <div className='row mt-md-8 mb-10 w-100 px-5 px-md-0 mb-4 justify-content-between'>
                        <div className='col-md-5 mb-10 mb-md-0'>
                          <div className='w-50'>
                            <Skeleton
                              height={20}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                          </div>
                          <div className='mt-2'>
                            <Skeleton
                              height={30}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                          </div>
                        </div>
                        <div className='col-md-5'>
                          <div className='w-50'>
                            <Skeleton
                              height={20}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                          </div>
                          <div className='mt-2'>
                            <Skeleton
                              height={30}
                              baseColor='var(--kt-skeleton-bg-color)'
                              highlightColor='var(--kt-skeleton-highlight-bg-color)'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Row>
                        <Col md={6}>
                          <TextInput
                            fieldType='text'
                            label={formatMessage({ id: 'Case name' })}
                            fieldName='projectname'
                            formik={formik}
                            placeholder={formatMessage({ id: 'Enter Case name' })}
                            toolTipText={formatMessage({
                              id: 'Enter the name of the case that aligns with your organization’s structure and use case.',
                            })}
                            isStarRequired={true}
                            isTooltipForModal={true}
                          />
                        </Col>
                        <Col md={6}>
                          <InputPeriod
                            label={formatMessage({
                              id: 'Case-Specific Retention Period',
                            })}
                            toolTipText={formatMessage({
                              id: 'Specify the default file retention period for records associated with this project. This determines how long files will be kept before archiving or deletion.',
                            })}
                            fieldName='projectValue'
                            placeholder={formatMessage({ id: 'Case-Specific Retention Period' })}
                            placeholderSelect={initialValues?.projectPeriod}
                            formik={formik}
                            isTooltipForModal={true}
                            options={periodOptions}
                            selectOptionFieldName='projectPeriod'
                          />
                        </Col>
                      </Row>
                      <div className='form-check form-check-custom form-check-solid form-check-primary form-switch mt-4'>
                        <label className='togglebtn mx-0'>
                          <Field
                            className='togglebtn__input'
                            name='isLegalHold'
                            type='checkbox'
                            formik={formik}
                            onChange={(event: any) => {
                              formik.handleChange(event)
                              const isChecked = event.target.checked

                              if (!isChecked) {
                                formik.setFieldValue('legalHoldExpiryValue', '')
                                formik.setFieldValue('legalHoldExpiryPeriod', null)
                              }
                            }}
                          />
                          <div className='togglebtn__fill'></div>
                        </label>
                        <div className='d-flex flex-column justify-content-end mt-2'>
                          <div className='d-flex'>
                            <label className='form-check-label fw-semibold font-size-13 title-notification'>
                              {formatMessage({ id: 'Legal Hold' })}
                            </label>
                          </div>
                          <label className='form-check-label text-clr58 fw-normal fs-7'>
                            {formatMessage({
                              id: "Ability to mark certain documents as 'On Legal Hold' to prevent them from being modified or deleted",
                            })}
                          </label>
                        </div>
                      </div>
                      <Row className='mt-4'>
                        <Col md={6}>
                          <InputPeriod
                            label={formatMessage({
                              id: 'Legal Hold Expiry Period',
                            })}
                            toolTipText={formatMessage({
                              id: 'Set the default expiry date for legal holds applied to files and records belonging to this department. This can be extended if necessary during legal proceedings.',
                            })}
                            fieldName='legalHoldExpiryValue'
                            placeholder={formatMessage({ id: 'Legal Hold Expiry Period' })}
                            placeholderSelect={initialValues?.legalHoldExpiryPeriod}
                            formik={formik}
                            options={periodOptions}
                            isTooltipForModal={true}
                            selectOptionFieldName='legalHoldExpiryPeriod'
                            disabled={!formik.values.isLegalHold}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer className='container px-0'>
                <div className='d-flex flex-md-row flex-column w-100 justify-content-end px-md-0 px-0 gap-3 mt-4'>
                  <button
                    type='button'
                    className='btn bg-light text-muted w-auto order-2 order-md-1 custom-button-property'
                    onClick={() => {
                      setIsActive(false)
                      setEditDetailsData(null)
                      formik.resetForm()
                    }}
                  >
                    {formatMessage({ id: 'Cancel' })}
                  </button>
                  <button
                    type='submit'
                    className='btn bg-primary text-white w-auto order-1 order-md-2 custom-button-property'
                    disabled={(!formik.isValid && formik.values.isLegalHold) || isLoading}
                    onClick={formik.submitForm}
                  >
                    {!isLoading ? (
                      <span className='indicator-label font-size-13 p-0'>
                        {formatMessage({
                          id: editDetailsData ? 'Edit Case' : 'Create Case',
                        })}
                      </span>
                    ) : (
                      <span className='indicator-label font-size-13 p-0'>
                        {formatMessage({ id: 'Please wait...' })}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default AddProjectModal
